import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Play,
  Trophy,
  Users,
  Star,
  ArrowRight,
  ArrowLeft,
  Zap,
  Target,
  Award,
  Gamepad2,
  TrendingUp,
  Clock,
} from "lucide-react";
import gamesData from "../../../utils/user-data/games.json";
import "./games.css";
import Footer from "../../../components/footer";
import Navbar from "../../../components/navbar";

const GamesPage = () => {
  const [currentGameIndex, setCurrentGameIndex] = useState(0);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState("all");
  const [isLoading, setIsLoading] = useState(false);

  const gameCategories = [
    { id: "all", name: "All Games", icon: Gamepad2 },
    { id: "battle-royale", name: "Battle Royale", icon: Target },
    { id: "fighting", name: "Fighting", icon: Zap },
    { id: "sports", name: "Sports", icon: Trophy },
  ];

  const gameCategoriesMap = {
    FC25: "sports",
    "CALL OF DUTY": "battle-royale",
    "PUBG MOBILE": "battle-royale",
    TEKKEN: "fighting",
    "NBA 2K": "sports",
    "Mortal Combat": "fighting",
    FORTNITE: "fighting",
    EFOOTBALL: "sports",
  };

  const filteredGames = gamesData.games.filter(
    (game) =>
      activeCategory === "all" ||
      gameCategoriesMap[game.name] === activeCategory
  );

  const getVideoIdFromURL = (url) => {
    const regex =
      /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:embed\/|watch\?v=|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match && match[1] ? match[1] : null;
  };

  const handleGameSelect = (index) => {
    setCurrentGameIndex(index);
    setIsLoading(true);
    setTimeout(() => setIsLoading(false), 500);
  };

  const handleVideoOpen = () => {
    setIsVideoOpen(true);
  };

  const handleVideoClose = () => {
    setIsVideoOpen(false);
  };

  const nextGame = () => {
    setCurrentGameIndex((prev) => (prev + 1) % filteredGames.length);
  };

  const prevGame = () => {
    setCurrentGameIndex(
      (prev) => (prev - 1 + filteredGames.length) % filteredGames.length
    );
  };

  const currentGame = filteredGames[currentGameIndex];

  useEffect(() => {
    document.title = "Games - Cheda Gaming";
  }, []);

  return (
    <>
      <Navbar />
      <div className="games-page-container">
        {/* Background Effects */}
        <div className="games-background">
          <div className="background-overlay"></div>
          <div className="floating-particles"></div>
        </div>

        {/* Hero Section */}
        <section className="games-hero-section">
          <div className="games-hero-content">
            <div className="game-hero-badge">
              <Gamepad2 className="badge-icon" size={20} />
              <span>GAMING UNIVERSE</span>
            </div>

            <h1 className="games-hero-title">
              Discover <span className="title-highlight">Epic Games</span>
            </h1>

            <p className="games-hero-subtitle">
              Immerse yourself in the world of competitive gaming with our
              curated collection of premium titles
            </p>

            {/* Category Filter */}
            <div className="games-category-filter">
              {gameCategories.map((category) => (
                <button
                  key={category.id}
                  className={`category-btn ${
                    activeCategory === category.id ? "active" : ""
                  }`}
                  onClick={() => setActiveCategory(category.id)}
                >
                  <category.icon size={16} />
                  <span>{category.name}</span>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Main Games Section */}
        <section className="games-main-section">
          <div className="games-content-wrapper">
            {/* Featured Game Display */}
            <div className="featured-game-display">
              <div className="game-showcase-container">
                <div className="game-background-image">
                  <img
                    src={currentGame.backgroundImage}
                    alt={currentGame.name}
                  />
                  <div className="game-overlay"></div>
                </div>

                <div className="game-showcase-content">
                  <div className="game-info-panel">
                    <div className="game-header">
                      <div className="game-year-badge">
                        <span>{new Date().getFullYear()}</span>
                      </div>
                      <h2 className="game-title">{currentGame.name}</h2>
                    </div>

                    <p className="game-description">
                      {currentGame.description}
                    </p>

                    <div className="game-page-stats">
                      <div className="stat-item">
                        <Users className="stat-icon" size={16} />
                        <span>Players Online</span>
                        <strong>1.5K</strong>
                      </div>
                      <div className="stat-item">
                        <Star className="stat-icon" size={16} />
                        <span>Rating</span>
                        <strong>4.8/5</strong>
                      </div>
                    </div>

                    <div className="game-actions">
                      <button
                        className="play-video-btn"
                        onClick={handleVideoOpen}
                      >
                        <Play size={20} />
                        <span>Watch Trailer</span>
                      </button>

                      <Link
                        to="/tournaments#join-tournaments-section"
                        className="earn-money-btn"
                      >
                        <Trophy size={20} />
                        <span>Start Earning</span>
                      </Link>
                    </div>
                  </div>

                  <div className="game-visual-panel">
                    <div className="game-image-container">
                      <img
                        src={currentGame.gameImage}
                        alt={currentGame.name}
                        className={isLoading ? "loading" : ""}
                      />
                      <div className="game-image-overlay">
                        <div className="game-status">
                          <div className="status-indicator"></div>
                          <span>Live</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation Controls */}
              <div className="game-navigation">
                <button className="nav-btn prev-btn" onClick={prevGame}>
                  <ArrowLeft size={20} />
                </button>

                <div className="game-progress">
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${
                          ((currentGameIndex + 1) / filteredGames.length) * 100
                        }%`,
                      }}
                    ></div>
                  </div>
                  <span className="progress-text">
                    {currentGameIndex + 1} of {filteredGames.length}
                  </span>
                </div>

                <button className="nav-btn next-btn" onClick={nextGame}>
                  <ArrowRight size={20} />
                </button>
              </div>
            </div>

            {/* Games Grid */}
            <div className="games-grid-section">
              <div className="section-header">
                <h3>All Available Games</h3>
                <p>Choose your favorite game and start competing</p>
              </div>

              <div className="games-grid">
                {filteredGames.map((game, index) => (
                  <div
                    key={index}
                    className={`game-card ${
                      currentGameIndex === index ? "active" : ""
                    }`}
                    onClick={() => handleGameSelect(index)}
                  >
                    <div className="game-card-image">
                      <img src={game.gameImage} alt={game.name} />
                      <div className="game-card-overlay">
                        <div className="game-card-badge">
                          <TrendingUp size={16} />
                          <span>Popular</span>
                        </div>
                      </div>
                    </div>

                    <div className="game-card-content">
                      <h4>{game.name}</h4>
                      <div className="game-card-stats">
                        <span className="stat">
                          <Clock size={12} />
                          Active
                        </span>
                        <span className="stat">
                          <Award size={12} />
                          Tournaments
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Video Modal */}
        {isVideoOpen && (
          <div className="video-modal-overlay" onClick={handleVideoClose}>
            <div
              className="video-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="modal-close-btn" onClick={handleVideoClose}>
                &times;
              </button>
              <div className="video-wrapper">
                <iframe
                  className="youtube-video"
                  width="560"
                  height="315"
                  src={`https://www.youtube.com/embed/${getVideoIdFromURL(currentGame.videoURL)}`}
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default GamesPage;
